import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import P from '@leverege/promise';
import Unit from '@leverege/unit';
import { TICK_ACTIONS } from "./Constants.js";
import TimelineUtil from "../TimelineUtil.js";
const DEFAULT_TICK_INTERVAL = 1000;
export default function useTick(opts) {
  const {
    onChangeNow,
    onChangeRange,
    range,
    tickInterval,
    ticks,
    tickWindowDuration
  } = opts;
  const timerId = useRef(null);
  const isTicking = useRef(false);
  const isPlayingRef = useRef(false);
  const tickIntervalMs = useMemo(() => {
    if (tickInterval) {
      return Unit.convert(tickInterval.value, tickInterval.unit, 'ms');
    }
    return DEFAULT_TICK_INTERVAL;
  }, [tickInterval]);
  const tickTimes = useMemo(() => ticks.map(tick => tick.date), [ticks]);
  const tickMs = useMemo(() => {
    if (tickWindowDuration) {
      return Unit.convert(tickWindowDuration.value, tickWindowDuration.unit, 'ms');
    }
    return null;
  }, [tickWindowDuration]);
  const defaultState = useMemo(() => {
    return {
      scrubTime: range.start,
      ticking: false,
      isPlaying: false
    };
  }, [range]);
  const [{
    scrubTime,
    ticking,
    isPlaying
  }, dispatch] = useReducer(tickReducer, defaultState);
  const callbacks = useMemo(() => {
    return new Set();
  }, []);
  const tickCallbacks = useRef(callbacks);
  const onTick = useCallback(async scrubTime => {
    isTicking.current = true;
    dispatch(startTick(TimelineUtil.coerceEpoch(scrubTime)));
    try {
      await P.map(Array.from(tickCallbacks.current), cb => {
        return cb(scrubTime);
      });
      dispatch(endTick());
    } catch (err) {
      dispatch(tickError(err));
    } finally {
      isTicking.current = false;
    }
  }, []);
  const onPlayPause = useCallback(({
    value
  }) => {
    dispatch(setPlay(value));
    isPlayingRef.current = value;
  }, []);
  const onJumpToNow = useCallback(async () => {
    const now = Date.now();
    dispatch(init(now));
    onChangeRange({
      value: 'last24Hours'
    });
    onChangeNow({
      value: now
    });
    setTimeout(onTick, 1, now);
  }, [onChangeNow, onChangeRange]);
  useEffect(() => {
    dispatch(init(TimelineUtil.coerceEpoch(scrubTime)));
  }, []);
  useEffect(() => {
    if (isPlaying) {
      const tick = async scrubTime => {
        if (isTicking.current || !isPlayingRef.current) {
          return;
        }
        let next = tickMs ? TimelineUtil.coerceDateTime(scrubTime + tickMs) : TimelineUtil.findNextDate(scrubTime, tickTimes);
        next = next || tickTimes[0];
        await onTick(next);
        timerId.current = setTimeout(() => {
          tick(next);
        }, tickIntervalMs);
      };
      tick(scrubTime);
    }
  }, [isPlaying, tickIntervalMs, tickMs, tickTimes, onTick]);
  const addTickCallback = useCallback(cb => {
    tickCallbacks.current.add(cb);
  }, []);
  const removeTickCallback = useCallback(cb => {
    tickCallbacks.current.delete(cb);
  }, []);
  useEffect(() => {
    const parsedScrub = TimelineUtil.coerceEpoch(scrubTime);
    const parsedStart = TimelineUtil.coerceEpoch(range.start);
    const parsedEnd = TimelineUtil.coerceEpoch(range.end);
    if (parsedScrub < parsedStart) {
      onTick(parsedStart);
    } else if (parsedScrub > parsedEnd) {
      onTick(parsedEnd);
    }
  }, [range]);
  return {
    addTickCallback,
    isPlaying,
    onJumpToNow,
    onPlayPause,
    onTick,
    removeTickCallback,
    scrubTime,
    ticking
  };
}
function tickReducer(state, action) {
  const {
    type
  } = action;
  switch (type) {
    case TICK_ACTIONS.INIT:
      {
        const {
          payload: {
            scrubTime
          }
        } = action;
        return {
          ...state,
          scrubTime,
          ticking: false,
          error: null,
          isPlaying: false
        };
      }
    case TICK_ACTIONS.TICKING:
      {
        const {
          payload: {
            scrubTime
          }
        } = action;
        return {
          ...state,
          ticking: true,
          scrubTime
        };
      }
    case TICK_ACTIONS.TICKED:
      {
        return {
          ...state,
          ticking: false
        };
      }
    case TICK_ACTIONS.TICK_ERROR:
      {
        const {
          payload: {
            error
          }
        } = action;
        return {
          ...state,
          ticking: false,
          error: error.message
        };
      }
    case TICK_ACTIONS.PLAY_PAUSE:
      {
        const {
          payload: {
            isPlaying
          }
        } = action;
        return {
          ...state,
          isPlaying
        };
      }
    default:
      return state;
  }
}
function init(scrubTime) {
  return {
    type: TICK_ACTIONS.INIT,
    payload: {
      scrubTime
    }
  };
}
function startTick(scrubTime) {
  return {
    type: TICK_ACTIONS.TICKING,
    payload: {
      scrubTime
    }
  };
}
function endTick() {
  return {
    type: TICK_ACTIONS.TICKED
  };
}
function setPlay(isPlaying) {
  return {
    type: TICK_ACTIONS.PLAY_PAUSE,
    payload: {
      isPlaying
    }
  };
}
function tickError(error) {
  return {
    type: TICK_ACTIONS.TICK_ERROR,
    payload: {
      error
    }
  };
}